import React, { useState, useCallback, useEffect } from 'react';
import { Typography, Box, Button, useMediaQuery, useTheme, Grid, FormControl, CircularProgress } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SelectField } from '../../../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../../../Components/Form/Form.types';
import { useStyles } from '../../../../../../../Components/Field/Field.styles';
import { SelectOption } from '../../../../../../../Components/Form/Form.types';
import { StepButtonBar } from '../../../EditLegalListWizard/StepButtonBar';
import UpdateConnectionInfo from '../../../UpdateConnectionWizard/UpdateConnectionInfo';
import { useAptorApi } from '../../../../../../../Api';
import { IGridQueryRequest, OrderDirection } from '../../../../../../../Api/AptorApi';
import OurLawCheckListInfo from './OurLawCheckListInfo';

const PRIMARY = '#c4122f';

interface Step1SelectCompanyUnitProps {
  onNext: (unit: SelectOption) => void;
  onBack: () => void;
  onCancel: () => void;
  isFirstStep: boolean;
  lawId: number;
  selectedCompanyUnit?: SelectOption;
  getCompanyUnitName: (formatMessage: any, isSingular: boolean) => string;
  updateChecklistInfo?: any;
  currentStepName?: string;
  setUpdateChecklistInfo?: () => void;
}

const Step1SelectCompanyUnit: React.FC<Step1SelectCompanyUnitProps> = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  lawId,
  selectedCompanyUnit,
  getCompanyUnitName,
  updateChecklistInfo,
  currentStepName,
  setUpdateChecklistInfo,
}) => {
  const [localSelected, setLocalSelected] = useState<SelectOption | undefined>(selectedCompanyUnit);
  const [companyUnitOptions, setCompanyUnitOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);
  const classes = useStyles();
  const { api } = useAptorApi();

  useEffect(() => {
    const loadCompanyUnits = async () => {
      setIsLoading(true);
      try {
        const request: IGridQueryRequest = {
          page: 0,
          pageSize: 100,
          orderBy: 'name',
          orderDirection: OrderDirection.Ascending,
        };
        const result = await api.searchLawCompanyUnits<any>(lawId, request);
        if (result && result.data) {
          const options = result.data.map((unit: any) => ({
            label: unit.name,
            value: unit.id,
          }));
          setCompanyUnitOptions(options);
        }
      } catch (error) {
        console.error('Failed to load company units:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanyUnits();
  }, [lawId, api]);

  const handleChange = useCallback((val: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
    if (val && !Array.isArray(val)) {
      setLocalSelected(val as SelectOption);
    }
  }, []);

  const handleNext = () => {
    if (localSelected) {
      onNext(localSelected);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: "350px"
          }}
        >


          <Box style={{ maxWidth: '320px', width: '100%' }}>
            <Grid container spacing={3} justifyContent="flex-start">
              <Grid
                item
                xs={12}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <FormControl className={classes.formControl} style={{ width: '100%' }}>
                  <SelectField
                    name="companyUnit"
                    placeholder={formatMessage(
                      { id: 'law-portal.manage.update-laws.dialog.companyUnit.placeholder' },
                      { alias: getCompanyUnitName(formatMessage, true) },
                    )}
                    options={companyUnitOptions}
                    isMulti={false}
                    errorTexts={[]}
                    initialState={localSelected}
                    onChange={handleChange}
                  />
                </FormControl>
              </Grid>

            </Grid>
          </Box>
        </div>

        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            maxHeight: "500px",
            overflow: 'auto',
          }}
        >

          <OurLawCheckListInfo
            updateConnectionInfo={updateChecklistInfo}
            currentStepName={currentStepName}
            onUpdate={setUpdateChecklistInfo}
          />
        </div>
      </div>

      <Box>
        <StepButtonBar
          onCancel={() => { }}
          onBack={isFirstStep ? undefined : onBack}
          onNext={handleNext}
          nextLabel="form.next"
          nextDisabled={!localSelected || isLoading}
          formatMessage={formatMessage}
          showBack={false}
        />
      </Box>
    </div>
  );
};

export default Step1SelectCompanyUnit;
